/**
 * CoachPlaybook - TeamSettings Component
 * Version: 0.5.145
 * Description: Team identity, jersey management, opponent scouting
 * 
 * v0.5.121 Changes:
 * - Added logo positioning controls (Y position, size, rotation, flip X/Y)
 * - Controls only show when logo is uploaded
 * - Reset button clears all logo transforms
 * 
 * v0.5.120 Changes:
 * - Now embeds as regular tab instead of modal overlay
 * - onClose is optional (no X button or Cancel when embedded)
 * - Container styling adapts for modal vs embedded mode
 */

import React, { useState, useEffect, useRef } from 'react';
import JerseyIcon, { JERSEY_FONTS, TeamPresets, DEFAULT_JERSEY_TEMPLATE, DEFAULT_POSITIONS, getGoogleFontsLink, BORDER_LIMITS } from '../common/JerseyIcon';
import { getSetting, setSetting } from '../../utils/db';

// Storage keys
const JERSEY_TEMPLATES_KEY = 'jerseyTemplates';
const ACTIVE_TEMPLATE_KEY = 'activeJerseyTemplate';
const TEAM_LOGO_KEY = 'teamLogo';
const TEAM_NAME_KEY = 'teamName';
const OPPONENTS_KEY = 'opponents';
const CUSTOM_PRESETS_KEY = 'customJerseyPresets';

// Skill categories for scouting
const SKILL_CATEGORIES = [
  { key: 'shooting', label: 'Shooting', icon: '🎯', color: '#22c55e' },
  { key: 'driving', label: 'Driving', icon: '💨', color: '#3b82f6' },
  { key: 'passing', label: 'Passing', icon: '👁️', color: '#8b5cf6' },
  { key: 'defense', label: 'Defense', icon: '🛡️', color: '#ef4444' },
  { key: 'rebounding', label: 'Rebounding', icon: '🏀', color: '#f59e0b' },
  { key: 'athleticism', label: 'Speed', icon: '⚡', color: '#06b6d4' },
  { key: 'bbiq', label: 'BBIQ', icon: '🧠', color: '#ec4899' }
];

// Skill level options
const SKILL_LEVELS = [
  { value: '', label: 'Select Level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'recreational', label: 'Recreational' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'competitive', label: 'Competitive' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'elite', label: 'Elite' },
  { value: 'pro', label: 'Professional' }
];

// Default opponent template
const DEFAULT_OPPONENT = {
  id: '',
  name: '',
  abbreviation: '',
  league: '',
  leagueSkillLevel: '',
  actualSkillLevel: '',
  fillColor: '#dc2626',
  outlineColor: '#991b1b',
  numberColor: '#ffffff',
  numberOutline: '#991b1b',
  textColor: '#ffffff',
  fontFamily: 'Impact, Haettenschweiler, sans-serif',
  logoUrl: null,
  players: []
};

// Default player for scouting (with positioning)
const DEFAULT_SCOUTED_PLAYER = {
  id: '',
  name: 'Dave',
  jerseyNumber: '0',
  position: '',
  gender: 'M', // M, F, or empty
  notes: '',
  useTeamJersey: true,
  numberY: DEFAULT_POSITIONS.numberY,
  nameY: DEFAULT_POSITIONS.nameY,
  numberSize: DEFAULT_POSITIONS.numberSize,
  nameSize: DEFAULT_POSITIONS.nameSize,
  skills: {
    shooting: 50,
    driving: 50,
    passing: 50,
    defense: 50,
    rebounding: 50,
    athleticism: 50,
    bbiq: 50
  }
};

// Stat display component with Top 3 green, Bottom 3 red
const StatDisplay = ({ skills }) => {
  if (!skills) return null;
  
  const sortedSkills = Object.entries(skills)
    .map(([key, value]) => {
      const category = SKILL_CATEGORIES.find(c => c.key === key);
      return { key, value, ...category };
    })
    .filter(s => s.label)
    .sort((a, b) => b.value - a.value);
  
  const top3Keys = sortedSkills.slice(0, 3).map(s => s.key);
  const bottom3Keys = sortedSkills.slice(-3).map(s => s.key);
  
  return (
    <div className="grid grid-cols-2 gap-2">
      {SKILL_CATEGORIES.map(skill => {
        const value = skills[skill.key] || 50;
        const isTop3 = top3Keys.includes(skill.key);
        const isBottom3 = bottom3Keys.includes(skill.key) && !isTop3;
        
        let bgColor = 'bg-slate-800';
        let textColor = 'text-slate-300';
        let valueColor = 'text-white';
        
        if (isTop3) {
          bgColor = 'bg-green-900/50';
          textColor = 'text-green-300';
          valueColor = 'text-green-400';
        } else if (isBottom3) {
          bgColor = 'bg-red-900/50';
          textColor = 'text-red-300';
          valueColor = 'text-red-400';
        }
        
        return (
          <div key={skill.key} className={`flex items-center justify-between px-3 py-2 rounded-lg ${bgColor}`}>
            <div className="flex items-center gap-2">
              <span className="text-sm">{skill.icon}</span>
              <span className={`text-xs font-medium ${textColor}`}>{skill.label}</span>
            </div>
            <span className={`text-sm font-bold ${valueColor}`}>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const TeamSettings = ({ onClose = null }) => {
  const [mainView, setMainView] = useState('myteam');
  
  // My Team state
  const [teamName, setTeamName] = useState('My Team');
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('home');
  const [teamLogo, setTeamLogo] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('colors');
  
  // Opponents state
  const [opponents, setOpponents] = useState([]);
  const [editingOpponent, setEditingOpponent] = useState(null);
  const [showOpponentForm, setShowOpponentForm] = useState(false);
  const [opponentTab, setOpponentTab] = useState('info');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [playerTab, setPlayerTab] = useState('info');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  
  // Custom presets
  const [customPresets, setCustomPresets] = useState([]);
  
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  const opponentLogoRef = useRef(null);
  
  // Preview state
  const [previewNumber, setPreviewNumber] = useState('23');
  const [previewName, setPreviewName] = useState('SMITH');
  const [previewView, setPreviewView] = useState('back');
  
  // Front/Back independence state
  const [editingView, setEditingView] = useState('both'); // 'front', 'back', or 'both'
  const [linkFrontBack, setLinkFrontBack] = useState(true); // When true, changes apply to both views
  
  // Load saved data
  useEffect(() => {
    const loadData = async () => {
      try {
        const savedTeamName = await getSetting(TEAM_NAME_KEY);
        const savedTemplates = await getSetting(JERSEY_TEMPLATES_KEY);
        const savedActiveId = await getSetting(ACTIVE_TEMPLATE_KEY);
        const savedLogo = await getSetting(TEAM_LOGO_KEY);
        const savedOpponents = await getSetting(OPPONENTS_KEY);
        
        if (savedTeamName) setTeamName(savedTeamName);
        
        if (savedTemplates && savedTemplates.length > 0) {
          const migratedTemplates = savedTemplates.map(t => ({
            ...DEFAULT_JERSEY_TEMPLATE,
            ...t,
            numberY: t.numberY ?? DEFAULT_POSITIONS.numberY,
            nameY: t.nameY ?? DEFAULT_POSITIONS.nameY,
            numberSize: t.numberSize ?? DEFAULT_POSITIONS.numberSize,
            nameSize: t.nameSize ?? DEFAULT_POSITIONS.nameSize
          }));
          setTemplates(migratedTemplates);
        } else {
          const defaults = [
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'home', name: 'Home' },
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'away', name: 'Away', fillColor: '#ffffff', outlineColor: '#2563eb', numberColor: '#2563eb', numberOutline: '#1e40af', textColor: '#2563eb' },
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'alternate', name: 'Alternate', fillColor: '#1f2937', outlineColor: '#374151', numberColor: '#f59e0b', numberOutline: '#1f2937', textColor: '#f59e0b' }
          ];
          setTemplates(defaults);
          await setSetting(JERSEY_TEMPLATES_KEY, defaults);
        }
        
        if (savedActiveId) setActiveTemplateId(savedActiveId);
        if (savedLogo) setTeamLogo(savedLogo);
        
        if (savedOpponents) {
          const migratedOpponents = savedOpponents.map(o => ({
            ...DEFAULT_OPPONENT,
            ...o,
            league: o.league || '',
            leagueSkillLevel: o.leagueSkillLevel || '',
            actualSkillLevel: o.actualSkillLevel || '',
            players: (o.players || []).map(p => ({
              ...DEFAULT_SCOUTED_PLAYER,
              ...p,
              gender: p.gender || 'M',
              useTeamJersey: p.useTeamJersey !== false,
              numberY: p.numberY ?? DEFAULT_POSITIONS.numberY,
              nameY: p.nameY ?? DEFAULT_POSITIONS.nameY,
              numberSize: p.numberSize ?? DEFAULT_POSITIONS.numberSize,
              nameSize: p.nameSize ?? DEFAULT_POSITIONS.nameSize
            }))
          }));
          setOpponents(migratedOpponents);
        }
        
        // Load custom presets
        const savedPresets = await getSetting(CUSTOM_PRESETS_KEY);
        if (savedPresets) {
          setCustomPresets(savedPresets);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);
  
  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = getGoogleFontsLink();
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  
  useEffect(() => {
    if (templates.length > 0 && !editingTemplate) {
      const active = templates.find(t => t.id === activeTemplateId) || templates[0];
      // Always merge with defaults to ensure all fields exist
      setEditingTemplate({ 
        ...DEFAULT_JERSEY_TEMPLATE,
        ...active,
        numberY: typeof active.numberY === 'number' ? active.numberY : DEFAULT_POSITIONS.numberY,
        nameY: typeof active.nameY === 'number' ? active.nameY : DEFAULT_POSITIONS.nameY,
        numberSize: typeof active.numberSize === 'number' ? active.numberSize : DEFAULT_POSITIONS.numberSize,
        nameSize: typeof active.nameSize === 'number' ? active.nameSize : DEFAULT_POSITIONS.nameSize
      });
    }
  }, [templates, activeTemplateId]);
  
  // Save functions
  const saveTemplates = async (newTemplates) => {
    setTemplates(newTemplates);
    await setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
  };
  
  const saveOpponents = async (newOpponents) => {
    setOpponents(newOpponents);
    await setSetting(OPPONENTS_KEY, newOpponents);
  };
  
  const saveTeamName = async (name) => {
    setTeamName(name);
    await setSetting(TEAM_NAME_KEY, name);
  };
  
  // Template CRUD
  const handleAddTemplate = async () => {
    if (!newTemplateName.trim()) return;
    const newId = `custom_${Date.now()}`;
    const newTemplate = { ...DEFAULT_JERSEY_TEMPLATE, id: newId, name: newTemplateName.trim().slice(0, 16) };
    const newTemplates = [...templates, newTemplate];
    await saveTemplates(newTemplates);
    setEditingTemplate({ ...newTemplate });
    setActiveTemplateId(newId);
    await setSetting(ACTIVE_TEMPLATE_KEY, newId);
    setNewTemplateName('');
    setShowAddModal(false);
  };
  
  const handleDuplicateTemplate = async () => {
    if (!editingTemplate) return;
    const newId = `copy_${Date.now()}`;
    const duplicated = { ...editingTemplate, id: newId, name: `${editingTemplate.name} Copy`.slice(0, 16) };
    const newTemplates = [...templates, duplicated];
    await saveTemplates(newTemplates);
    setEditingTemplate({ ...duplicated });
    setActiveTemplateId(newId);
    await setSetting(ACTIVE_TEMPLATE_KEY, newId);
  };
  
  const handleRenameTemplate = async () => {
    if (!newTemplateName.trim() || !editingTemplate) return;
    const updated = { ...editingTemplate, name: newTemplateName.trim().slice(0, 16) };
    const newTemplates = templates.map(t => t.id === updated.id ? updated : t);
    await saveTemplates(newTemplates);
    setEditingTemplate(updated);
    setNewTemplateName('');
    setShowRenameModal(false);
  };
  
  const handleRemoveTemplate = async () => {
    if (!templateToDelete || templates.length <= 1) return;
    const newTemplates = templates.filter(t => t.id !== templateToDelete.id);
    await saveTemplates(newTemplates);
    if (editingTemplate?.id === templateToDelete.id) {
      setEditingTemplate({ ...newTemplates[0] });
      setActiveTemplateId(newTemplates[0].id);
      await setSetting(ACTIVE_TEMPLATE_KEY, newTemplates[0].id);
    }
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };
  
  // Logo upload
  const handleLogoUpload = async (e, isOpponent = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 500 * 1024) { alert('Logo must be under 500KB'); return; }
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      if (isOpponent && editingOpponent) {
        setEditingOpponent(prev => ({ ...prev, logoUrl: base64 }));
      } else {
        setTeamLogo(base64);
        await setSetting(TEAM_LOGO_KEY, base64);
      }
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveLogo = async () => {
    setTeamLogo(null);
    await setSetting(TEAM_LOGO_KEY, null);
  };
  
  // Fields that are view-specific (can differ between front and back)
  const VIEW_SPECIFIC_FIELDS = new Set([
    'numberY', 'numberSize', 'numberColor', 'numberOutline', 'numberOutline2', 
    'numberBorderWeight', 'numberBorderWeight2',
    'nameY', 'nameSize', 'textColor', 'textOutline', 'textOutline2',
    'nameBorderWeight', 'nameBorderWeight2',
    'fontFamily'
  ]);
  
  // Map legacy field names to view settings field names
  const FIELD_NAME_MAP = {
    nameY: 'textY',
    nameSize: 'textSize',
    nameBorderWeight: 'textBorderWeight',
    nameBorderWeight2: 'textBorderWeight2'
  };
  
  const updateField = (field, value) => {
    const isViewSpecific = VIEW_SPECIFIC_FIELDS.has(field);
    
    if (isViewSpecific && !linkFrontBack) {
      // When front/back are independent, update the specific view's settings
      const viewKey = editingView === 'both' ? 'front' : editingView;
      const mappedField = FIELD_NAME_MAP[field] || field;
      
      setEditingTemplate(prev => {
        const updatedTemplate = { ...prev };
        
        if (editingView === 'both') {
          // Update both front and back
          updatedTemplate.front = { ...(prev.front || {}), [mappedField]: value };
          updatedTemplate.back = { ...(prev.back || {}), [mappedField]: value };
        } else {
          // Update specific view
          updatedTemplate[viewKey] = { ...(prev[viewKey] || {}), [mappedField]: value };
        }
        
        // Also update legacy field for backward compatibility
        updatedTemplate[field] = value;
        
        return updatedTemplate;
      });
    } else {
      // Linked mode or global field - update at root level (and sync to both views)
      setEditingTemplate(prev => {
        const updatedTemplate = { ...prev, [field]: value };
        
        // If it's a view-specific field and we're in linked mode, sync to both views
        if (isViewSpecific && linkFrontBack) {
          const mappedField = FIELD_NAME_MAP[field] || field;
          updatedTemplate.front = { ...(prev.front || {}), [mappedField]: value };
          updatedTemplate.back = { ...(prev.back || {}), [mappedField]: value };
        }
        
        return updatedTemplate;
      });
    }
  };
  
  // Get the effective value for a field based on current editing view
  const getFieldValue = (field, defaultValue) => {
    if (!editingTemplate) return defaultValue;
    
    const isViewSpecific = VIEW_SPECIFIC_FIELDS.has(field);
    
    if (isViewSpecific && !linkFrontBack && editingView !== 'both') {
      const viewSettings = editingTemplate[editingView];
      const mappedField = FIELD_NAME_MAP[field] || field;
      if (viewSettings && viewSettings[mappedField] !== undefined) {
        return viewSettings[mappedField];
      }
    }
    
    // Fall back to root-level value
    return editingTemplate[field] ?? defaultValue;
  };
  
  // Check if front and back have different fonts (for warning)
  const hasFontMismatch = () => {
    if (!editingTemplate || linkFrontBack) return false;
    const frontFont = editingTemplate.front?.fontFamily || editingTemplate.fontFamily;
    const backFont = editingTemplate.back?.fontFamily || editingTemplate.fontFamily;
    return frontFont && backFont && frontFont !== backFont;
  };
  
  const handleSelectTemplate = (templateId) => {
    if (editingTemplate) {
      const newTemplates = templates.map(t => t.id === editingTemplate.id ? { ...editingTemplate } : t);
      setTemplates(newTemplates);
      setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
    }
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Always merge with defaults to ensure all fields exist
      setEditingTemplate({ 
        ...DEFAULT_JERSEY_TEMPLATE,
        ...template,
        // Force position values to be numbers (fix for spacing bug)
        numberY: typeof template.numberY === 'number' ? template.numberY : DEFAULT_POSITIONS.numberY,
        nameY: typeof template.nameY === 'number' ? template.nameY : DEFAULT_POSITIONS.nameY,
        numberSize: typeof template.numberSize === 'number' ? template.numberSize : DEFAULT_POSITIONS.numberSize,
        nameSize: typeof template.nameSize === 'number' ? template.nameSize : DEFAULT_POSITIONS.nameSize
      });
      setActiveTemplateId(templateId);
    }
  };
  
  const applyPreset = (presetName) => {
    const preset = TeamPresets[presetName];
    if (preset && editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev, ...preset,
        textColor: preset.numberColor,
        textOutline: preset.numberOutline || preset.outlineColor
      }));
    }
  };
  
  // Custom preset functions
  const applyCustomPreset = (preset) => {
    if (preset && editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev,
        fillColor: preset.fillColor,
        outlineColor: preset.outlineColor,
        numberColor: preset.numberColor,
        numberOutline: preset.numberOutline,
        numberOutline2: preset.numberOutline2,
        textColor: preset.textColor,
        textOutline: preset.textOutline,
        textOutline2: preset.textOutline2,
        fontFamily: preset.fontFamily,
        colorPalette: preset.colorPalette || []
      }));
    }
  };
  
  const saveAsPreset = async () => {
    if (!newPresetName.trim() || !editingTemplate) return;
    const preset = {
      id: `preset_${Date.now()}`,
      name: newPresetName.trim(),
      fillColor: editingTemplate.fillColor,
      outlineColor: editingTemplate.outlineColor,
      numberColor: editingTemplate.numberColor,
      numberOutline: editingTemplate.numberOutline,
      numberOutline2: editingTemplate.numberOutline2,
      textColor: editingTemplate.textColor,
      textOutline: editingTemplate.textOutline,
      textOutline2: editingTemplate.textOutline2,
      fontFamily: editingTemplate.fontFamily,
      colorPalette: editingTemplate.colorPalette || []
    };
    const newPresets = [...customPresets, preset];
    setCustomPresets(newPresets);
    await setSetting(CUSTOM_PRESETS_KEY, newPresets);
    setNewPresetName('');
    setShowSavePresetModal(false);
  };
  
  const deleteCustomPreset = async (presetId) => {
    const newPresets = customPresets.filter(p => p.id !== presetId);
    setCustomPresets(newPresets);
    await setSetting(CUSTOM_PRESETS_KEY, newPresets);
  };
  
  // Color palette functions
  const addToPalette = (color) => {
    if (!editingTemplate) return;
    const currentPalette = editingTemplate.colorPalette || [];
    if (currentPalette.length >= 6) return;
    if (currentPalette.includes(color)) return;
    setEditingTemplate(prev => ({
      ...prev,
      colorPalette: [...currentPalette, color]
    }));
  };
  
  const removeFromPalette = (colorIndex) => {
    if (!editingTemplate) return;
    const currentPalette = editingTemplate.colorPalette || [];
    setEditingTemplate(prev => ({
      ...prev,
      colorPalette: currentPalette.filter((_, i) => i !== colorIndex)
    }));
  };
  
  const applyPaletteColor = (color, field) => {
    if (!editingTemplate) return;
    setEditingTemplate(prev => ({ ...prev, [field]: color }));
  };
  
  // Set default jersey
  const setAsDefaultJersey = async () => {
    if (!editingTemplate) return;
    const newTemplates = templates.map(t => ({
      ...t,
      isDefault: t.id === editingTemplate.id
    }));
    setTemplates(newTemplates);
    await setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
    setEditingTemplate(prev => ({ ...prev, isDefault: true }));
  };
  
  const resetPositioning = () => {
    setEditingTemplate(prev => ({ ...prev, ...DEFAULT_POSITIONS }));
  };
  
  // Opponent CRUD
  const handleAddOpponent = () => {
    setEditingOpponent({ ...DEFAULT_OPPONENT, id: `opp_${Date.now()}`, players: [] });
    setOpponentTab('info');
    setShowOpponentForm(true);
  };
  
  const handleEditOpponent = (opponent) => {
    setEditingOpponent({ 
      ...DEFAULT_OPPONENT,
      ...opponent, 
      players: (opponent.players || []).map(p => ({
        ...DEFAULT_SCOUTED_PLAYER,
        ...p
      }))
    });
    setOpponentTab('info');
    setShowOpponentForm(true);
  };
  
  const handleSaveOpponent = async () => {
    if (!editingOpponent?.name.trim()) { alert('Opponent name is required'); return; }
    
    const opponent = {
      ...editingOpponent,
      abbreviation: editingOpponent.abbreviation || editingOpponent.name.slice(0, 3).toUpperCase()
    };
    
    const existing = opponents.find(o => o.id === opponent.id);
    const newOpponents = existing
      ? opponents.map(o => o.id === opponent.id ? opponent : o)
      : [...opponents, opponent];
    
    await saveOpponents(newOpponents);
    setShowOpponentForm(false);
    setEditingOpponent(null);
  };
  
  const handleCancelOpponent = () => {
    setShowOpponentForm(false);
    setEditingOpponent(null);
  };
  
  const handleDeleteOpponent = async (opponentId) => {
    if (!window.confirm('Delete this opponent and all scouted players?')) return;
    const newOpponents = opponents.filter(o => o.id !== opponentId);
    await saveOpponents(newOpponents);
  };
  
  const applyPresetToOpponent = (presetName) => {
    const preset = TeamPresets[presetName];
    if (preset && editingOpponent) {
      setEditingOpponent(prev => ({
        ...prev, ...preset,
        textColor: preset.numberColor
      }));
    }
  };
  
  // Player scouting CRUD
  const handleAddPlayer = () => {
    setEditingPlayer({ ...DEFAULT_SCOUTED_PLAYER, id: `player_${Date.now()}` });
    setPlayerTab('info');
    setShowPlayerForm(true);
  };
  
  const handleEditPlayer = (player) => {
    setEditingPlayer({ ...DEFAULT_SCOUTED_PLAYER, ...player });
    setPlayerTab('info');
    setShowPlayerForm(true);
  };
  
  const handleSavePlayer = () => {
    if (!editingPlayer?.name.trim()) { alert('Player name is required'); return; }
    
    const existingIdx = editingOpponent.players.findIndex(p => p.id === editingPlayer.id);
    const newPlayers = existingIdx >= 0
      ? editingOpponent.players.map(p => p.id === editingPlayer.id ? editingPlayer : p)
      : [...editingOpponent.players, editingPlayer];
    
    setEditingOpponent(prev => ({ ...prev, players: newPlayers }));
    setShowPlayerForm(false);
    setEditingPlayer(null);
  };
  
  const handleDeletePlayer = (playerId) => {
    if (!window.confirm('Delete this scouted player?')) return;
    setEditingOpponent(prev => ({
      ...prev,
      players: prev.players.filter(p => p.id !== playerId)
    }));
  };
  
  const updatePlayerSkill = (skillKey, value) => {
    setEditingPlayer(prev => ({
      ...prev,
      skills: { ...prev.skills, [skillKey]: value }
    }));
  };
  
  const updatePlayerField = (field, value) => {
    setEditingPlayer(prev => ({ ...prev, [field]: value }));
  };
  
  const getAverageSkill = (skills) => {
    const values = Object.values(skills || {});
    if (values.length === 0) return 50;
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  };
  
  const getSkillColor = (value) => {
    if (value >= 80) return '#22c55e';
    if (value >= 60) return '#84cc16';
    if (value >= 40) return '#f59e0b';
    if (value >= 20) return '#f97316';
    return '#ef4444';
  };
  
  const getSkillLevelLabel = (value) => {
    const level = SKILL_LEVELS.find(l => l.value === value);
    return level?.label || '';
  };
  
  // Final save
  const handleSave = async () => {
    if (editingTemplate) {
      const newTemplates = templates.map(t => t.id === editingTemplate.id ? { ...editingTemplate } : t);
      await saveTemplates(newTemplates);
      await setSetting(ACTIVE_TEMPLATE_KEY, editingTemplate.id);
    }
    if (onClose) onClose();
  };
  
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }
  
  // Embedded mode (no onClose) vs Modal mode (with onClose)
  const isModal = !!onClose;
  
  return (
    <div className={isModal 
      ? "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4"
      : "h-full flex flex-col bg-slate-900"
    }>
      <div className={isModal
        ? "bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col"
        : "flex-1 overflow-hidden flex flex-col"
      }>
        {/* Title Bar */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-3 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl">⚙️</span>
            <h2 className="text-lg font-bold text-white tracking-wide">Team Settings</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Team Name Section */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-700">
          <span className="text-2xl">👕</span>
          <div>
            <input type="text" value={teamName} onChange={(e) => saveTeamName(e.target.value)}
              className="bg-transparent text-xl font-bold text-white border-b-2 border-transparent hover:border-slate-600 focus:border-blue-500 focus:outline-none px-1 -ml-1"
              placeholder="Team Name" />
            <p className="text-xs text-slate-500 mt-1">Click to edit team name</p>
          </div>
        </div>
        
        {/* Main View Tabs */}
        <div className="flex border-b border-slate-700">
          <button onClick={() => setMainView('myteam')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${mainView === 'myteam' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
            🏠 My Team Jerseys
          </button>
          <button onClick={() => setMainView('opponents')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${mainView === 'opponents' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
            🆚 Opponents ({opponents.length})
          </button>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          
          {/* ===== MY TEAM VIEW ===== */}
          {mainView === 'myteam' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Template Selector - Fixed at top */}
              <div className="flex items-center gap-2 flex-wrap p-6 pb-4 bg-slate-900/95 border-b border-slate-800">
                {templates.map(template => (
                  <button key={template.id} onClick={() => handleSelectTemplate(template.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${editingTemplate?.id === template.id ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    <div className="w-3 h-3 rounded-full inline-block mr-2" style={{ backgroundColor: template.fillColor }} />
                    {template.name}
                  </button>
                ))}
                <button onClick={() => setShowAddModal(true)} className="px-4 py-2 rounded-lg font-medium bg-green-600 text-white hover:bg-green-500">+ New</button>
              </div>
              
              {/* Two-column layout with independent scrolling */}
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
                {/* Left: Preview - Sticky/Fixed */}
                <div className="p-6 overflow-y-auto bg-slate-900 lg:border-r lg:border-slate-800">
                  <div className="space-y-4 lg:sticky lg:top-0">
                  <div className="bg-slate-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Preview</h3>
                      <div className="flex gap-1">
                        {['front', 'back'].map(v => (
                          <button key={v} onClick={() => setPreviewView(v)}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${previewView === v ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                          </button>
                        ))}
                    </div>
                    </div>
                    <div className="flex justify-center py-4 bg-slate-900 rounded-lg">
                      <JerseyIcon size={180} number={previewNumber} view={previewView}
                        teamName={previewView === 'front' ? teamName.toUpperCase() : ''}
                        lastName={previewView === 'back' ? previewName : ''}
                        fillColor={editingTemplate?.fillColor} outlineColor={editingTemplate?.outlineColor}
                        numberColor={editingTemplate?.numberColor} numberOutline={editingTemplate?.numberOutline}
                        numberOutline2={editingTemplate?.numberOutline2}
                        textColor={editingTemplate?.textColor} textOutline={editingTemplate?.textOutline}
                        textOutline2={editingTemplate?.textOutline2}
                        fontFamily={editingTemplate?.fontFamily} logoUrl={previewView === 'front' ? teamLogo : null}
                        logoY={editingTemplate?.logoY ?? 65}
                        logoSize={editingTemplate?.logoSize ?? 50}
                        logoRotation={editingTemplate?.logoRotation ?? 0}
                        logoFlipX={editingTemplate?.logoFlipX ?? false}
                        logoFlipY={editingTemplate?.logoFlipY ?? false}
                        numberY={editingTemplate?.numberY ?? DEFAULT_POSITIONS.numberY} 
                        nameY={editingTemplate?.nameY ?? DEFAULT_POSITIONS.nameY}
                        numberSize={editingTemplate?.numberSize ?? DEFAULT_POSITIONS.numberSize} 
                        nameSize={editingTemplate?.nameSize ?? DEFAULT_POSITIONS.nameSize}
                        jerseyBorderWeight={editingTemplate?.jerseyBorderWeight ?? DEFAULT_POSITIONS.jerseyBorderWeight}
                        numberBorderWeight={editingTemplate?.numberBorderWeight ?? DEFAULT_POSITIONS.numberBorderWeight}
                        numberBorderWeight2={editingTemplate?.numberBorderWeight2 ?? 0}
                        nameBorderWeight={editingTemplate?.nameBorderWeight ?? DEFAULT_POSITIONS.nameBorderWeight}
                        nameBorderWeight2={editingTemplate?.nameBorderWeight2 ?? 0}
                        front={editingTemplate?.front}
                        back={editingTemplate?.back} />
                    </div>
                    <div className="flex gap-3 mt-4">
                      <div className="flex-1">
                        <label className="block text-xs text-slate-400 mb-1">Number</label>
                        <input type="text" value={previewNumber} onChange={(e) => setPreviewNumber(e.target.value.slice(0, 3))} maxLength={3}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-center" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-slate-400 mb-1">Name</label>
                        <input type="text" value={previewName} onChange={(e) => setPreviewName(e.target.value.toUpperCase().slice(0, 12))} maxLength={12}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-center" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Template Actions */}
                  <div className="bg-slate-800 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-slate-400 mb-3">Template Actions</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <button onClick={() => { setNewTemplateName(editingTemplate?.name || ''); setShowRenameModal(true); }}
                        className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">✏️ Rename</button>
                      <button onClick={handleDuplicateTemplate}
                        className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">📋 Duplicate</button>
                      <button onClick={() => { setTemplateToDelete(editingTemplate); setShowDeleteConfirm(true); }}
                        disabled={templates.length <= 1}
                        className="px-3 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/30 disabled:opacity-50">🗑️ Delete</button>
                    </div>
                  </div>
                  
                  {/* Logo Upload */}
                  <div className="bg-slate-800 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-slate-400 mb-3">Team Logo</h3>
                    {teamLogo ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <img src={teamLogo} alt="Logo" className="w-16 h-16 object-contain bg-white rounded-lg p-1"/>
                          <div className="flex flex-col gap-2">
                            <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">Change</button>
                            <button onClick={handleRemoveLogo} className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/30">Remove</button>
                          </div>
                        </div>
                        
                        {/* Logo Controls */}
                        <div className="border-t border-slate-700 pt-4 space-y-3">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Logo Controls</p>
                          
                          {/* Position Y */}
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-slate-400 w-16">Position</span>
                            <input 
                              type="range" 
                              min="10" max="90" 
                              value={editingTemplate?.logoY ?? 65}
                              onChange={(e) => updateField('logoY', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                            <span className="text-xs text-slate-500 w-8 text-right">{editingTemplate?.logoY ?? 65}%</span>
                          </div>
                          
                          {/* Size */}
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-slate-400 w-16">Size</span>
                            <input 
                              type="range" 
                              min="10" max="100" 
                              value={editingTemplate?.logoSize ?? 50}
                              onChange={(e) => updateField('logoSize', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                            <span className="text-xs text-slate-500 w-8 text-right">{editingTemplate?.logoSize ?? 50}%</span>
                          </div>
                          
                          {/* Rotation */}
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-slate-400 w-16">Rotate</span>
                            <input 
                              type="range" 
                              min="-180" max="180" 
                              value={editingTemplate?.logoRotation ?? 0}
                              onChange={(e) => updateField('logoRotation', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                            <span className="text-xs text-slate-500 w-8 text-right">{editingTemplate?.logoRotation ?? 0}°</span>
                          </div>
                          
                          {/* Flip buttons */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-16">Flip</span>
                            <button
                              onClick={() => updateField('logoFlipX', !(editingTemplate?.logoFlipX))}
                              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                editingTemplate?.logoFlipX 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                              }`}
                            >
                              ↔️ Horizontal
                            </button>
                            <button
                              onClick={() => updateField('logoFlipY', !(editingTemplate?.logoFlipY))}
                              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                editingTemplate?.logoFlipY 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                              }`}
                            >
                              ↕️ Vertical
                            </button>
                            {(editingTemplate?.logoRotation !== 0 || editingTemplate?.logoFlipX || editingTemplate?.logoFlipY) && (
                              <button
                                onClick={() => {
                                  updateField('logoRotation', 0);
                                  updateField('logoFlipX', false);
                                  updateField('logoFlipY', false);
                                }}
                                className="px-3 py-1.5 text-xs bg-slate-700 text-orange-400 rounded-lg hover:bg-slate-600"
                              >
                                ↺ Reset
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => fileInputRef.current?.click()}
                        className="w-full py-6 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-blue-500 hover:text-blue-400">
                        📤 Upload Logo (max 500KB)
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, false)} className="hidden"/>
                  </div>
                  </div>
                </div>
                
                {/* Right: Controls - Scrollable */}
                <div className="p-6 overflow-y-auto bg-slate-900">
                  <div className="space-y-4">
                  <div className="flex gap-1 bg-slate-800 p-1 rounded-xl">
                    {[{ id: 'colors', label: '🎨 Colors' }, { id: 'positioning', label: '📐 Layout' }, { id: 'presets', label: '⚡ Presets' }].map(tab => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  
                  {activeTab === 'colors' && (
                    <div className="space-y-4">
                      {/* Front/Back toggle for colors too */}
                      {!linkFrontBack && (
                        <div className="bg-gradient-to-r from-slate-800 to-slate-800/80 rounded-xl p-3 border border-slate-700">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-400">Editing colors for:</span>
                            <div className="flex gap-1">
                              {['front', 'back', 'both'].map(v => (
                                <button 
                                  key={v}
                                  onClick={() => {
                                    setEditingView(v);
                                    if (v !== 'both') setPreviewView(v);
                                  }}
                                  className={`px-2 py-1 text-xs rounded ${editingView === v ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}
                                >
                                  {v === 'both' ? 'Both' : v.charAt(0).toUpperCase() + v.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Jersey Base Colors - Always global */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">Jersey Colors <span className="text-xs text-slate-500">(applies to both)</span></h3>
                        <div className="space-y-3">
                          {[{ key: 'fillColor', label: 'Body Color' }, { key: 'outlineColor', label: 'Outline/Trim' }].map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-3">
                              <input type="color" value={editingTemplate?.[key] || '#000000'} onChange={(e) => updateField(key, e.target.value)}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600" />
                              <div className="flex-1">
                                <label className="block text-sm text-white mb-1">{label}</label>
                                <input type="text" value={editingTemplate?.[key] || ''} onChange={(e) => updateField(key, e.target.value)}
                                  className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono text-sm" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Number & Text Colors - View-specific when unlinked */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">
                          Number Colors
                          {!linkFrontBack && editingView !== 'both' && (
                            <span className="ml-2 text-xs text-blue-400">({editingView} only)</span>
                          )}
                        </h3>
                        <div className="space-y-3">
                          {[{ key: 'numberColor', label: 'Number Fill' }, { key: 'numberOutline', label: 'Number Stroke' }].map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-3">
                              <input type="color" value={getFieldValue(key, '#000000')} onChange={(e) => updateField(key, e.target.value)}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600" />
                              <div className="flex-1">
                                <label className="block text-sm text-white mb-1">{label}</label>
                                <input type="text" value={getFieldValue(key, '')} onChange={(e) => updateField(key, e.target.value)}
                                  className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono text-sm" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">
                          Name Text Colors
                          {!linkFrontBack && editingView !== 'both' && (
                            <span className="ml-2 text-xs text-blue-400">({editingView} only)</span>
                          )}
                        </h3>
                        <div className="space-y-3">
                          {[{ key: 'textColor', label: 'Text Fill' }, { key: 'textOutline', label: 'Text Stroke' }].map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-3">
                              <input type="color" value={getFieldValue(key, '#000000')} onChange={(e) => updateField(key, e.target.value)}
                                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600" />
                              <div className="flex-1">
                                <label className="block text-sm text-white mb-1">{label}</label>
                                <input type="text" value={getFieldValue(key, '')} onChange={(e) => updateField(key, e.target.value)}
                                  className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono text-sm" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-3">
                          Font Style
                          {!linkFrontBack && editingView !== 'both' && (
                            <span className="ml-2 text-xs text-blue-400">({editingView} only)</span>
                          )}
                        </h3>
                        {/* Group fonts by category */}
                        {['Athletic', 'Block', 'Classic', 'Collegiate', 'Street', 'Marker', 'Script'].map(category => {
                          const fontsInCategory = JERSEY_FONTS.filter(f => f.category === category);
                          if (fontsInCategory.length === 0) return null;
                          return (
                            <div key={category} className="mb-4">
                              <div className="text-xs text-slate-500 mb-2 uppercase tracking-wider">
                                {category === 'Street' ? '🏙️ Street/Graffiti' : 
                                 category === 'Marker' ? '✏️ Marker/Whiteboard' : 
                                 category === 'Script' ? '🖌️ Script/Brush' : category}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {fontsInCategory.map(font => (
                                  <button key={font.id} onClick={() => updateField('fontFamily', font.family)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${getFieldValue('fontFamily', '') === font.family ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                                    style={{ fontFamily: font.family }}>
                                    {font.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Font mismatch warning in colors tab too */}
                        {hasFontMismatch() && (
                          <div className="mt-3 p-2 bg-yellow-600/20 border border-yellow-500/50 rounded-lg">
                            <div className="flex items-center gap-2 text-yellow-400 text-xs">
                              <span>⚠️</span>
                              <span>Front and back use different fonts</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'positioning' && (
                    <div className="space-y-4">
                      {/* Front/Back Independence Control */}
                      <div className="bg-gradient-to-r from-slate-800 to-slate-800/80 rounded-xl p-4 border border-slate-700">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-white">🎽 Front/Back Settings</h3>
                          <button 
                            onClick={() => {
                              setLinkFrontBack(!linkFrontBack);
                              if (!linkFrontBack) setEditingView('both');
                            }}
                            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                              linkFrontBack 
                                ? 'bg-green-600/30 text-green-400 border border-green-500/50' 
                                : 'bg-purple-600/30 text-purple-400 border border-purple-500/50'
                            }`}
                          >
                            {linkFrontBack ? '🔗 Linked' : '🔓 Independent'}
                          </button>
                        </div>
                        
                        {!linkFrontBack && (
                          <div className="flex gap-1 bg-slate-900/50 p-1 rounded-lg">
                            {[
                              { id: 'front', label: '👕 Front', color: 'blue' },
                              { id: 'back', label: '🔢 Back', color: 'blue' },
                              { id: 'both', label: '⚡ Both', color: 'purple' }
                            ].map(view => (
                              <button 
                                key={view.id}
                                onClick={() => {
                                  setEditingView(view.id);
                                  setPreviewView(view.id === 'both' ? 'back' : view.id);
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                                  editingView === view.id 
                                    ? `bg-${view.color}-600 text-white` 
                                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                                }`}
                              >
                                {view.label}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {!linkFrontBack && editingView !== 'both' && (
                          <p className="text-xs text-slate-500 mt-2">
                            Editing <span className="text-blue-400 font-medium">{editingView === 'front' ? 'Front' : 'Back'}</span> view only
                          </p>
                        )}
                        
                        {/* Font mismatch warning */}
                        {hasFontMismatch() && (
                          <div className="mt-3 p-2 bg-yellow-600/20 border border-yellow-500/50 rounded-lg">
                            <div className="flex items-center gap-2 text-yellow-400 text-xs">
                              <span>⚠️</span>
                              <span>Front and back use different fonts - this may look inconsistent</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-slate-800 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-slate-400">Number Position & Size</h3>
                          <button onClick={resetPositioning} className="px-3 py-1 text-xs bg-slate-700 text-slate-400 rounded hover:bg-slate-600">Reset</button>
                        </div>
                        {[{ key: 'numberY', label: 'Vertical Position', min: 20, max: 80 }, { key: 'numberSize', label: 'Number Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                          <div key={key} className="mb-6">
                            <div className="flex justify-between mb-2">
                              <label className="text-sm text-white">{label}</label>
                              <span className="text-sm text-blue-400 font-mono">{getFieldValue(key, 50)}%</span>
                            </div>
                            <input type="range" min={min} max={max} value={getFieldValue(key, 50)} onChange={(e) => updateField(key, parseInt(e.target.value))}
                              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                          </div>
                        ))}
                      </div>
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">Name Position & Size</h3>
                        {[{ key: 'nameY', label: 'Vertical Position', min: 0, max: 50 }, { key: 'nameSize', label: 'Name Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                          <div key={key} className="mb-6">
                            <div className="flex justify-between mb-2">
                              <label className="text-sm text-white">{label}</label>
                              <span className="text-sm text-blue-400 font-mono">{getFieldValue(key, 50)}%</span>
                            </div>
                            <input type="range" min={min} max={max} value={getFieldValue(key, 50)} onChange={(e) => updateField(key, parseInt(e.target.value))}
                              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                          </div>
                        ))}
                      </div>
                      
                      {/* Border Weight Controls */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">Jersey Border</h3>
                        <div className="mb-4">
                          <div className="flex justify-between mb-2">
                            <label className="text-sm text-white">Jersey Outline</label>
                            <span className="text-sm text-orange-400 font-mono">{editingTemplate?.jerseyBorderWeight ?? 12}px</span>
                          </div>
                          <input type="range" min={BORDER_LIMITS.jersey.min} max={BORDER_LIMITS.jersey.max} 
                            value={editingTemplate?.jerseyBorderWeight ?? 12} 
                            onChange={(e) => updateField('jerseyBorderWeight', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500" />
                        </div>
                      </div>
                      
                      {/* Number Borders - Inner + Outer */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">Number Borders</h3>
                        
                        {/* Inner Border */}
                        <div className="mb-4 pb-4 border-b border-slate-700">
                          <div className="flex items-center gap-3 mb-2">
                            <input type="color" value={getFieldValue('numberOutline', '#1e40af')} 
                              onChange={(e) => updateField('numberOutline', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-600" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <label className="text-sm text-white">Inner Border</label>
                                <span className="text-sm text-blue-400 font-mono">{getFieldValue('numberBorderWeight', 6)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.numberInner.min} max={BORDER_LIMITS.numberInner.max} 
                            value={getFieldValue('numberBorderWeight', 6)} 
                            onChange={(e) => updateField('numberBorderWeight', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                        </div>
                        
                        {/* Outer Border (Double Border) */}
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <input type="color" value={getFieldValue('numberOutline2', '#000000')} 
                              onChange={(e) => updateField('numberOutline2', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-600" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <label className="text-sm text-white">Outer Border <span className="text-slate-500">(double)</span></label>
                                <span className="text-sm text-purple-400 font-mono">{getFieldValue('numberBorderWeight2', 0)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.numberOuter.min} max={BORDER_LIMITS.numberOuter.max} 
                            value={getFieldValue('numberBorderWeight2', 0)} 
                            onChange={(e) => updateField('numberBorderWeight2', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                          <p className="text-xs text-slate-500 mt-1">Set to 0 to disable outer border</p>
                        </div>
                      </div>
                      
                      {/* Name Borders - Inner + Outer */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">Name Borders</h3>
                        
                        {/* Inner Border */}
                        <div className="mb-4 pb-4 border-b border-slate-700">
                          <div className="flex items-center gap-3 mb-2">
                            <input type="color" value={getFieldValue('textOutline', '#1e40af')} 
                              onChange={(e) => updateField('textOutline', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-600" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <label className="text-sm text-white">Inner Border</label>
                                <span className="text-sm text-blue-400 font-mono">{getFieldValue('nameBorderWeight', 4)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.nameInner.min} max={BORDER_LIMITS.nameInner.max} 
                            value={getFieldValue('nameBorderWeight', 4)} 
                            onChange={(e) => updateField('nameBorderWeight', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                        </div>
                        
                        {/* Outer Border (Double Border) */}
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <input type="color" value={getFieldValue('textOutline2', '#000000')} 
                              onChange={(e) => updateField('textOutline2', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-600" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <label className="text-sm text-white">Outer Border <span className="text-slate-500">(double)</span></label>
                                <span className="text-sm text-purple-400 font-mono">{getFieldValue('nameBorderWeight2', 0)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.nameOuter.min} max={BORDER_LIMITS.nameOuter.max} 
                            value={getFieldValue('nameBorderWeight2', 0)} 
                            onChange={(e) => updateField('nameBorderWeight2', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                          <p className="text-xs text-slate-500 mt-1">Set to 0 to disable outer border</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'presets' && (
                    <div className="space-y-4">
                      {/* Color Palette */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-slate-400">Team Color Palette</h3>
                          <span className="text-xs text-slate-500">{(editingTemplate?.colorPalette || []).length}/6</span>
                        </div>
                        <div className="flex gap-2 mb-3">
                          {(editingTemplate?.colorPalette || []).map((color, i) => (
                            <div key={i} className="relative group">
                              <button
                                className="w-10 h-10 rounded-lg border-2 border-slate-600 hover:border-blue-500 transition-colors"
                                style={{ backgroundColor: color }}
                                onClick={() => {
                                  // Show color picker options
                                }}
                                title={color}
                              />
                              <button
                                onClick={() => removeFromPalette(i)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >×</button>
                            </div>
                          ))}
                          {(editingTemplate?.colorPalette || []).length < 6 && (
                            <button
                              onClick={() => {
                                // Add current fill color to palette
                                addToPalette(editingTemplate?.fillColor);
                              }}
                              className="w-10 h-10 rounded-lg border-2 border-dashed border-slate-600 text-slate-500 hover:border-blue-500 hover:text-blue-400 transition-colors flex items-center justify-center"
                            >+</button>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => addToPalette(editingTemplate?.fillColor)} className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600">+ Body</button>
                          <button onClick={() => addToPalette(editingTemplate?.outlineColor)} className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600">+ Outline</button>
                          <button onClick={() => addToPalette(editingTemplate?.numberColor)} className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600">+ Number</button>
                          <button onClick={() => addToPalette(editingTemplate?.textColor)} className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600">+ Text</button>
                        </div>
                        {(editingTemplate?.colorPalette || []).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-700">
                            <p className="text-xs text-slate-500 mb-2">Apply palette color to:</p>
                            <div className="flex gap-2 flex-wrap">
                              {['fillColor', 'outlineColor', 'numberColor', 'numberOutline', 'textColor', 'textOutline'].map(field => (
                                <select key={field} onChange={(e) => e.target.value && applyPaletteColor(e.target.value, field)} value=""
                                  className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded border border-slate-600">
                                  <option value="">{field.replace('Color', '').replace('Outline', ' Outline')}</option>
                                  {(editingTemplate?.colorPalette || []).map((c, i) => (
                                    <option key={i} value={c}>{c}</option>
                                  ))}
                                </select>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Save as Preset & Default Jersey */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-3">Quick Actions</h3>
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => setShowSavePresetModal(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-500">
                            💾 Save as Preset
                          </button>
                          <button onClick={setAsDefaultJersey}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${editingTemplate?.isDefault ? 'bg-yellow-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                            {editingTemplate?.isDefault ? '⭐ Default Jersey' : '☆ Set as Default'}
                          </button>
                        </div>
                        {editingTemplate?.isDefault && (
                          <p className="text-xs text-yellow-400 mt-2">This jersey is used as the display default</p>
                        )}
                      </div>
                      
                      {/* Custom Presets */}
                      {customPresets.length > 0 && (
                        <div className="bg-slate-800 rounded-xl p-4">
                          <h3 className="text-sm font-semibold text-slate-400 mb-4">My Saved Presets</h3>
                          <div className="grid grid-cols-2 gap-3">
                            {customPresets.map(preset => (
                              <div key={preset.id} className="relative group">
                                <button onClick={() => applyCustomPreset(preset)}
                                  className="w-full flex items-center gap-3 px-4 py-3 bg-slate-700 rounded-lg hover:bg-slate-600">
                                  <div className="w-8 h-8 rounded-lg border-2" style={{ backgroundColor: preset.fillColor, borderColor: preset.outlineColor }} />
                                  <span className="text-white font-medium text-sm truncate">{preset.name}</span>
                                </button>
                                <button onClick={() => deleteCustomPreset(preset.id)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* NBA Team Presets */}
                      <div className="bg-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-slate-400 mb-4">NBA Team Presets</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.entries(TeamPresets).map(([name, colors]) => (
                            <button key={name} onClick={() => applyPreset(name)}
                              className="flex items-center gap-3 px-4 py-3 bg-slate-700 rounded-lg hover:bg-slate-600">
                              <div className="w-8 h-8 rounded-lg border-2" style={{ backgroundColor: colors.fillColor, borderColor: colors.outlineColor }} />
                              <span className="text-white font-medium">{name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* ===== OPPONENTS VIEW - LIST ===== */}
          {mainView === 'opponents' && !showOpponentForm && (
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Opponent Teams</h3>
                <button onClick={handleAddOpponent} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500">+ Add Opponent</button>
              </div>
              
              {opponents.length === 0 ? (
                <div className="text-center py-12 bg-slate-800 rounded-xl">
                  <div className="text-4xl mb-4">🆚</div>
                  <h4 className="text-lg font-semibold text-white mb-2">No Opponents Added</h4>
                  <p className="text-slate-400 mb-6">Add opponent teams to scout players</p>
                  <button onClick={handleAddOpponent} className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500">+ Add First Opponent</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {opponents.map(opponent => (
                    <div key={opponent.id} className="bg-slate-800 rounded-xl p-4">
                      <div className="flex items-start gap-4">
                        <JerseyIcon size={60} number="00" fillColor={opponent.fillColor} outlineColor={opponent.outlineColor}
                          numberColor={opponent.numberColor} numberOutline={opponent.numberOutline} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-semibold truncate">{opponent.name}</h4>
                            <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-400 font-mono">{opponent.abbreviation}</span>
                          </div>
                          {opponent.league && (
                            <p className="text-slate-500 text-xs mt-0.5">{opponent.league}</p>
                          )}
                          <p className="text-slate-400 text-sm mt-1">{opponent.players?.length || 0} scouted players</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => handleEditOpponent(opponent)} className="flex-1 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">✏️ Edit / Scout</button>
                        <button onClick={() => handleDeleteOpponent(opponent.id)} className="px-3 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/30">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* ===== OPPONENT FORM ===== */}
          {mainView === 'opponents' && showOpponentForm && editingOpponent && !showPlayerForm && (
            <div className="p-6 overflow-y-auto flex-1">
              {/* Prominent Team Name Header with Action Buttons */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <JerseyIcon size={50} number={editingOpponent.abbreviation?.slice(0, 2) || '??'}
                      fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                      numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline} />
                    <div>
                      <h2 className="text-2xl font-black text-white">
                        {editingOpponent.name || 'New Opponent'}
                      </h2>
                      <div className="flex items-center gap-2 mt-1">
                        {editingOpponent.abbreviation && (
                          <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white font-mono">{editingOpponent.abbreviation}</span>
                        )}
                        {editingOpponent.league && (
                          <span className="text-red-200 text-sm">{editingOpponent.league}</span>
                        )}
                        {editingOpponent.leagueSkillLevel && (
                          <span className="px-2 py-0.5 bg-white/10 rounded text-xs text-red-200">{getSkillLevelLabel(editingOpponent.leagueSkillLevel)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Action Buttons - All Together */}
                  <div className="flex items-center gap-2">
                    <button onClick={handleCancelOpponent}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors">
                      Cancel
                    </button>
                    <button onClick={handleSaveOpponent}
                      className="px-4 py-2 bg-white text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-colors">
                      💾 Save
                    </button>
                    <button onClick={handleCancelOpponent}
                      className="p-2 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-1 bg-slate-800 p-1 rounded-xl mb-6">
                <button onClick={() => setOpponentTab('info')} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${opponentTab === 'info' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white'}`}>📋 Team Info</button>
                <button onClick={() => setOpponentTab('players')} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${opponentTab === 'players' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white'}`}>👥 Scouted ({editingOpponent.players?.length || 0})</button>
              </div>
              
              {opponentTab === 'info' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-slate-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Jersey Preview</h3>
                      <div className="flex justify-center py-4 bg-slate-900 rounded-lg">
                        <JerseyIcon size={160} number="00" fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                          numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline}
                          textColor={editingOpponent.textColor} fontFamily={editingOpponent.fontFamily} logoUrl={editingOpponent.logoUrl} />
                      </div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-3">Opponent Logo</h3>
                      {editingOpponent.logoUrl ? (
                        <div className="flex items-center gap-4">
                          <img src={editingOpponent.logoUrl} alt="Logo" className="w-16 h-16 object-contain bg-white rounded-lg p-1"/>
                          <div className="flex flex-col gap-2">
                            <button onClick={() => opponentLogoRef.current?.click()} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm">Change</button>
                            <button onClick={() => setEditingOpponent(prev => ({ ...prev, logoUrl: null }))} className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => opponentLogoRef.current?.click()} className="w-full py-6 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-red-500 hover:text-red-400">📤 Upload Logo</button>
                      )}
                      <input ref={opponentLogoRef} type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, true)} className="hidden"/>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-4">Team Info</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-white mb-2">Team Name *</label>
                          <input type="text" value={editingOpponent.name} onChange={(e) => setEditingOpponent(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Central High Eagles" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                        </div>
                        <div>
                          <label className="block text-sm text-white mb-2">Abbreviation</label>
                          <input type="text" value={editingOpponent.abbreviation} maxLength={4}
                            onChange={(e) => setEditingOpponent(prev => ({ ...prev, abbreviation: e.target.value.toUpperCase() }))}
                            placeholder="e.g., CHE" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white uppercase" />
                        </div>
                      </div>
                    </div>
                    
                    {/* League Info Section */}
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-4">League Info</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-white mb-2">League Name</label>
                          <input type="text" value={editingOpponent.league || ''} 
                            onChange={(e) => setEditingOpponent(prev => ({ ...prev, league: e.target.value }))}
                            placeholder="e.g., YMCA Youth League, AAU, Rec League" 
                            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-white mb-2">League Skill Level</label>
                            <select value={editingOpponent.leagueSkillLevel || ''}
                              onChange={(e) => setEditingOpponent(prev => ({ ...prev, leagueSkillLevel: e.target.value }))}
                              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                              {SKILL_LEVELS.map(level => (
                                <option key={level.value} value={level.value}>{level.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-white mb-2">Actual Skill Level</label>
                            <select value={editingOpponent.actualSkillLevel || ''}
                              onChange={(e) => setEditingOpponent(prev => ({ ...prev, actualSkillLevel: e.target.value }))}
                              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                              {SKILL_LEVELS.map(level => (
                                <option key={level.value} value={level.value}>{level.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">League level = division rating. Actual level = your assessment of their true skill.</p>
                      </div>
                    </div>
                    
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-4">Jersey Colors</h3>
                      <div className="space-y-3">
                        {[{ key: 'fillColor', label: 'Body' }, { key: 'outlineColor', label: 'Outline' }, { key: 'numberColor', label: 'Number' }, { key: 'numberOutline', label: 'Number Stroke' }].map(({ key, label }) => (
                          <div key={key} className="flex items-center gap-3">
                            <input type="color" value={editingOpponent[key] || '#000000'} onChange={(e) => setEditingOpponent(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-10 h-10 rounded cursor-pointer border-2 border-slate-600" />
                            <span className="text-sm text-white flex-1">{label}</span>
                            <input type="text" value={editingOpponent[key] || ''} onChange={(e) => setEditingOpponent(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-24 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-300 font-mono text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-3">Quick Presets</h3>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(TeamPresets).slice(0, 6).map(([name, colors]) => (
                          <button key={name} onClick={() => applyPresetToOpponent(name)}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-lg hover:bg-slate-600 text-xs">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.fillColor }} />
                            <span className="text-white">{name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {opponentTab === 'players' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-slate-400 text-sm">Track key players for scouting</p>
                    <button onClick={handleAddPlayer} className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500">+ Add Player</button>
                  </div>
                  
                  {(!editingOpponent.players || editingOpponent.players.length === 0) ? (
                    <div className="text-center py-12 bg-slate-800 rounded-xl">
                      <div className="text-4xl mb-4">🔍</div>
                      <h4 className="text-lg font-semibold text-white mb-2">No Players Scouted</h4>
                      <p className="text-slate-400 mb-6">Add key players to track skills</p>
                      <button onClick={handleAddPlayer} className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500">+ Scout First Player</button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {editingOpponent.players.map(player => (
                        <div key={player.id} className="bg-slate-800 rounded-xl p-4">
                          <div className="flex items-start gap-4">
                            <JerseyIcon size={60} number={player.jerseyNumber || '0'} view="back"
                              lastName={player.name?.split(' ').pop()?.toUpperCase() || 'DAVE'}
                              fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                              numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline}
                              textColor={editingOpponent.textColor}
                              numberY={player.numberY} nameY={player.nameY}
                              numberSize={player.numberSize} nameSize={player.nameSize} />
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="text-white font-semibold">{player.name || 'Dave'}</h4>
                                <span className="text-slate-400 text-sm">#{player.jerseyNumber || '0'}</span>
                                {player.position && <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">{player.position}</span>}
                                {player.gender && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${player.gender === 'M' ? 'bg-blue-600/30 text-blue-300' : 'bg-pink-600/30 text-pink-300'}`}>
                                    {player.gender === 'M' ? '♂' : '♀'}
                                  </span>
                                )}
                                <span className="ml-auto text-lg font-bold" style={{ color: getSkillColor(getAverageSkill(player.skills)) }}>
                                  {getAverageSkill(player.skills)} OVR
                                </span>
                              </div>
                              
                              <StatDisplay skills={player.skills} />
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              <button onClick={() => handleEditPlayer(player)} className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">✏️</button>
                              <button onClick={() => handleDeletePlayer(player.id)} className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30">🗑️</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* ===== PLAYER SCOUTING FORM ===== */}
          {mainView === 'opponents' && showOpponentForm && showPlayerForm && editingPlayer && (
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-4 mb-6">
                <button onClick={() => { setShowPlayerForm(false); setEditingPlayer(null); }} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">← Back</button>
                <h3 className="text-lg font-semibold text-white">{editingOpponent.players.find(p => p.id === editingPlayer.id) ? 'Edit Player' : 'Scout New Player'}</h3>
              </div>
              
              <div className="flex gap-1 bg-slate-800 p-1 rounded-xl mb-6">
                <button onClick={() => setPlayerTab('info')} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${playerTab === 'info' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}>📋 Info</button>
                <button onClick={() => setPlayerTab('skills')} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${playerTab === 'skills' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}>📊 Skills</button>
                <button onClick={() => setPlayerTab('jersey')} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${playerTab === 'jersey' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}>👕 Jersey</button>
              </div>
              
              {playerTab === 'info' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-slate-800 rounded-xl p-6">
                    <div className="flex justify-center mb-6">
                      <JerseyIcon size={120} number={editingPlayer.jerseyNumber || '0'} view="back"
                        lastName={editingPlayer.name?.split(' ').pop()?.toUpperCase() || 'DAVE'}
                        fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                        numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline}
                        textColor={editingOpponent.textColor}
                        numberY={editingPlayer.numberY} nameY={editingPlayer.nameY}
                        numberSize={editingPlayer.numberSize} nameSize={editingPlayer.nameSize} />
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-white mb-2">Player Name *</label>
                        <input type="text" value={editingPlayer.name} onChange={(e) => updatePlayerField('name', e.target.value)}
                          placeholder="e.g., John Smith" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm text-white mb-2">Jersey #</label>
                          <input type="text" value={editingPlayer.jerseyNumber} maxLength={2}
                            onChange={(e) => updatePlayerField('jerseyNumber', e.target.value.replace(/\D/g, ''))}
                            placeholder="0" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center" />
                        </div>
                        <div>
                          <label className="block text-sm text-white mb-2">Position</label>
                          <select value={editingPlayer.position} onChange={(e) => updatePlayerField('position', e.target.value)}
                            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white">
                            <option value="">-</option>
                            <option value="PG">PG</option>
                            <option value="SG">SG</option>
                            <option value="SF">SF</option>
                            <option value="PF">PF</option>
                            <option value="C">C</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-white mb-2">Gender</label>
                          <div className="flex gap-1 h-[50px]">
                            <button type="button" onClick={() => updatePlayerField('gender', 'M')}
                              className={`flex-1 rounded-lg font-bold text-lg transition-colors ${editingPlayer.gender === 'M' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
                              ♂
                            </button>
                            <button type="button" onClick={() => updatePlayerField('gender', 'F')}
                              className={`flex-1 rounded-lg font-bold text-lg transition-colors ${editingPlayer.gender === 'F' ? 'bg-pink-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
                              ♀
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-white mb-2">Scouting Notes</label>
                        <textarea value={editingPlayer.notes || ''} onChange={(e) => updatePlayerField('notes', e.target.value)}
                          placeholder="Tendencies, weaknesses..." rows={3}
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Current Ratings</h3>
                      <span className="text-2xl font-bold" style={{ color: getSkillColor(getAverageSkill(editingPlayer.skills)) }}>
                        {getAverageSkill(editingPlayer.skills)} OVR
                      </span>
                    </div>
                    <StatDisplay skills={editingPlayer.skills} />
                    <p className="text-xs text-slate-500 mt-4 text-center">🟢 Top 3 strengths • 🔴 Bottom 3 weaknesses</p>
                  </div>
                </div>
              )}
              
              {playerTab === 'skills' && (
                <div className="bg-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Skill Ratings</h3>
                    <span className="text-2xl font-bold" style={{ color: getSkillColor(getAverageSkill(editingPlayer.skills)) }}>
                      {getAverageSkill(editingPlayer.skills)} OVR
                    </span>
                  </div>
                  <div className="space-y-5">
                    {SKILL_CATEGORIES.map(skill => (
                      <div key={skill.key}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{skill.icon}</span>
                            <span className="text-sm text-white font-medium">{skill.label}</span>
                          </div>
                          <span className="text-lg font-bold" style={{ color: getSkillColor(editingPlayer.skills?.[skill.key] || 50) }}>
                            {editingPlayer.skills?.[skill.key] || 50}
                          </span>
                        </div>
                        <input type="range" min="0" max="100" value={editingPlayer.skills?.[skill.key] || 50}
                          onChange={(e) => updatePlayerSkill(skill.key, parseInt(e.target.value))}
                          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                          style={{ 
                            background: `linear-gradient(to right, ${skill.color} 0%, ${skill.color} ${editingPlayer.skills?.[skill.key] || 50}%, #334155 ${editingPlayer.skills?.[skill.key] || 50}%, #334155 100%)`,
                            accentColor: skill.color
                          }} />
                        <div className="flex justify-between text-xs text-slate-500 mt-1">
                          <span>Poor</span>
                          <span>Average</span>
                          <span>Elite</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {playerTab === 'jersey' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-slate-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Jersey Preview</h3>
                    <div className="flex justify-center py-4 bg-slate-900 rounded-lg">
                      <JerseyIcon size={160} number={editingPlayer.jerseyNumber || '0'} view="back"
                        lastName={editingPlayer.name?.split(' ').pop()?.toUpperCase() || 'DAVE'}
                        fillColor={editingOpponent.fillColor} outlineColor={editingOpponent.outlineColor}
                        numberColor={editingOpponent.numberColor} numberOutline={editingOpponent.numberOutline}
                        textColor={editingOpponent.textColor}
                        numberY={editingPlayer.numberY} nameY={editingPlayer.nameY}
                        numberSize={editingPlayer.numberSize} nameSize={editingPlayer.nameSize} />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-slate-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-slate-400">Name Position & Size</h3>
                        <button onClick={() => {
                          updatePlayerField('nameY', DEFAULT_POSITIONS.nameY);
                          updatePlayerField('nameSize', DEFAULT_POSITIONS.nameSize);
                          updatePlayerField('numberY', DEFAULT_POSITIONS.numberY);
                          updatePlayerField('numberSize', DEFAULT_POSITIONS.numberSize);
                        }} className="px-3 py-1 text-xs bg-slate-700 text-slate-400 rounded hover:bg-slate-600">Reset</button>
                      </div>
                      {[{ key: 'nameY', label: 'Name Vertical', min: 10, max: 50 }, { key: 'nameSize', label: 'Name Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                        <div key={key} className="mb-4">
                          <div className="flex justify-between mb-2">
                            <label className="text-sm text-white">{label}</label>
                            <span className="text-sm text-green-400 font-mono">{editingPlayer[key] || 50}%</span>
                          </div>
                          <input type="range" min={min} max={max} value={editingPlayer[key] || 50}
                            onChange={(e) => updatePlayerField(key, parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-500" />
                        </div>
                      ))}
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-slate-400 mb-4">Number Position & Size</h3>
                      {[{ key: 'numberY', label: 'Number Vertical', min: 30, max: 80 }, { key: 'numberSize', label: 'Number Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                        <div key={key} className="mb-4">
                          <div className="flex justify-between mb-2">
                            <label className="text-sm text-white">{label}</label>
                            <span className="text-sm text-green-400 font-mono">{editingPlayer[key] || 50}%</span>
                          </div>
                          <input type="range" min={min} max={max} value={editingPlayer[key] || 50}
                            onChange={(e) => updatePlayerField(key, parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowPlayerForm(false); setEditingPlayer(null); }}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600">Cancel</button>
                <button onClick={handleSavePlayer}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-500">Save Player</button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {mainView === 'myteam' && (
          <div className="px-6 py-4 border-t border-slate-700 flex gap-3">
            {onClose && (
              <button onClick={onClose} className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600">Cancel</button>
            )}
            <button onClick={handleSave} className={`${onClose ? 'flex-1' : 'w-full'} px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500`}>Save Changes</button>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-slate-800 rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold text-white mb-4">New Jersey Template</h3>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Template name..." maxLength={16} autoFocus
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg">Cancel</button>
              <button onClick={handleAddTemplate} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">Create</button>
            </div>
          </div>
        </div>
      )}
      
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-slate-800 rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold text-white mb-4">Rename Template</h3>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="New name..." maxLength={16} autoFocus
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setShowRenameModal(false)} className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg">Cancel</button>
              <button onClick={handleRenameTemplate} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">Rename</button>
            </div>
          </div>
        </div>
      )}
      
      {showDeleteConfirm && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-slate-800 rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold text-red-400 mb-2">Delete Template?</h3>
            <p className="text-slate-300 mb-6">Delete "<strong>{templateToDelete.name}</strong>"? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg">Cancel</button>
              <button onClick={handleRemoveTemplate} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}
      
      {showSavePresetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-slate-800 rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold text-white mb-4">Save as Preset</h3>
            <p className="text-slate-400 text-sm mb-4">Save current colors and font as a reusable preset.</p>
            <input type="text" value={newPresetName} onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="Preset name..." maxLength={20} autoFocus
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white mb-4" />
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-700 rounded-lg">
              <div className="w-12 h-12 rounded-lg border-2" style={{ backgroundColor: editingTemplate?.fillColor, borderColor: editingTemplate?.outlineColor }} />
              <div className="text-sm">
                <div className="text-white font-medium">{editingTemplate?.name}</div>
                <div className="text-slate-400">Current colors will be saved</div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowSavePresetModal(false); setNewPresetName(''); }} className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg">Cancel</button>
              <button onClick={saveAsPreset} disabled={!newPresetName.trim()} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">Save Preset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
